import { useEffect, useState, useCallback, useRef, useMemo } from 'react';

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
import { Outlet, NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  AppShell,
  Text,
  Avatar,
  UnstyledButton,
  Box,
  Group,
  ScrollArea,
  Loader,
  Modal,
  Button,
  Stack,
  TextInput,
  Menu,
  Popover,
  Checkbox,
  SegmentedControl,
  Switch,
  Divider,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { BACKENDS, getBackendMode, setBackendMode } from '../../utils/backendConfig';
import {
  IconLayoutDashboard,
  IconSettings,
  IconLogout,
  IconPlus,
  IconPin,
  IconPinFilled,
  IconArchive,
  IconArchiveOff,
  IconCopy,
  IconTrash,
  IconDots,
  IconSearch,
  IconFilter,
  IconMessage,
  IconMessages,
  IconBuildingStore,
  IconCheck,
  IconCar,
  IconChevronDown,
  IconShield,
  IconShieldCheckFilled,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { AuthorizedDot } from './AuthorizedDot';
import { useConversations } from '../hooks/useConversations';
import { authApi, vehicleApi } from '../api/client';
import type { ConversationResponse, ShopResponse } from '../types';
import { processingState } from '../state/processingState';

// ─── Design tokens ────────────────────────────────────────────────────────────
const SIDEBAR_BG = '#111113';
const BORDER = '#27272A';
const BLUE = '#2563EB';
const SIDEBAR_WIDTH = 240;
// Collapsed desktop rail width (reopen button on top, gear on bottom).
const RAIL_WIDTH = 64;

// ─── Sidebar filter state (persisted to localStorage) ────────────────────────
type ConvStatusFilter = 'active' | 'repair' | 'archived' | 'completed';
type TimeRangeFilter = '7d' | '30d' | 'all';
type FilterState = {
  statuses: ConvStatusFilter[];     // empty list = default = active+repair
  timeRange: TimeRangeFilter;
  assignedToMe: boolean;
  hasSignature: boolean;
};
const FILTER_KEY = 'haist_sidebar_filters';
const DEFAULT_FILTERS: FilterState = {
  statuses: ['active', 'repair'],
  timeRange: '7d',
  assignedToMe: false,
  hasSignature: false,
};

function loadFilters(): FilterState {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_FILTERS, ...parsed };
  } catch { return DEFAULT_FILTERS; }
}

function saveFilters(f: FilterState) {
  try { localStorage.setItem(FILTER_KEY, JSON.stringify(f)); } catch { /* ignore */ }
}

function filtersAreDefault(f: FilterState): boolean {
  return (
    f.statuses.length === DEFAULT_FILTERS.statuses.length
    && f.statuses.every((s) => DEFAULT_FILTERS.statuses.includes(s))
    && f.timeRange === DEFAULT_FILTERS.timeRange
    && f.assignedToMe === DEFAULT_FILTERS.assignedToMe
    && f.hasSignature === DEFAULT_FILTERS.hasSignature
  );
}

// ─── Unread tracking (per-user, localStorage) ────────────────────────────────
const UNREAD_KEY = 'haist_last_read';

function getLastReadMap(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(UNREAD_KEY) ?? '{}'); } catch { return {}; }
}

function markConversationRead(conversationId: number, updatedAt: string) {
  const map = getLastReadMap();
  map[String(conversationId)] = updatedAt;
  localStorage.setItem(UNREAD_KEY, JSON.stringify(map));
}

function isUnread(conversationId: number, updatedAt: string): boolean {
  const map = getLastReadMap();
  const lastRead = map[String(conversationId)];
  if (!lastRead) return true; // never opened = unread
  return new Date(updatedAt) > new Date(lastRead);
}

function navItemStyle(isActive: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 16px',
    borderLeft: isActive ? `2px solid ${BLUE}` : '2px solid transparent',
    marginLeft: -1,
    textDecoration: 'none',
    background: isActive ? 'rgba(37,99,235,0.08)' : 'transparent',
    color: isActive ? '#F4F4F5' : '#71717A',
    fontWeight: isActive ? 600 : 400,
    fontSize: 13,
    transition: 'color 0.12s, background 0.12s',
  };
}

// Conversations workspace nav — a parent link plus the four view sub-links,
// expanded inline while on the page. Active sub-link tracks the ?view param
// (NavLink only matches pathname, so view highlighting is computed here).
const CONVERSATION_VIEWS: { v: string; label: string }[] = [
  { v: 'list', label: 'List' },
  { v: 'per-stage', label: 'Per Stage' },
  { v: 'per-charger', label: 'Per Charger' },
  { v: 'calendar', label: 'Calendar' },
  { v: 'stats', label: 'Statistics' },
];
function ConversationsNavGroup() {
  const location = useLocation();
  const navigate = useNavigate();
  const onPage = location.pathname === '/shop/conversations';
  const activeView = onPage ? new URLSearchParams(location.search).get('view') || 'list' : null;
  return (
    <>
      <NavLink to="/shop/conversations?view=list" style={() => navItemStyle(onPage)}>
        <IconMessages size={16} />
        Conversations
      </NavLink>
      {onPage && (
        <Box>
          {CONVERSATION_VIEWS.map((x) => {
            const active = activeView === x.v;
            return (
              <UnstyledButton
                key={x.v}
                onClick={() => navigate(`/shop/conversations?view=${x.v}`)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '5px 16px 5px 42px',
                  fontSize: 12,
                  color: active ? '#F4F4F5' : '#71717A',
                  fontWeight: active ? 600 : 400,
                  background: active ? 'rgba(37,99,235,0.08)' : 'transparent',
                }}
              >
                {x.label}
              </UnstyledButton>
            );
          })}
        </Box>
      )}
    </>
  );
}

// ─── Recent Conversations (per-device cache) ─────────────────────────────────
// Tracks the last N conversations the user opened on THIS device.
// Cached in localStorage so it survives navigation but is NOT shared
// across devices — intentional: "recent on this machine" is the useful
// signal. Stale labels (renamed conv, customer changed name) refresh
// the next time the user opens that conv.
type RecentConv = {
  id: number;
  owner: string | null;
  model: string | null;
  plate: string | null;
  charger: string | null;
  viewedAt: number;
};
// Scope the cache by shop_id — otherwise a multi-shop user (e.g. a datium
// account) accumulates every shop's conversations into one list and sees another
// shop's recent ROs after switching. Mirrors featureFlags' per-shop storage.
const RECENT_CONVS_KEY = (shopId: number | null | undefined) =>
  `haist_recent_conversations:${shopId ?? 'anon'}`;
const RECENT_CONVS_KEY_LEGACY = 'haist_recent_conversations'; // pre-scoping global key (cross-shop)
const RECENT_CONVS_MAX = 10;

function loadRecentConvs(shopId: number | null | undefined): RecentConv[] {
  try {
    const raw = localStorage.getItem(RECENT_CONVS_KEY(shopId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((r) => r && typeof r.id === 'number' && typeof r.viewedAt === 'number')
      .slice(0, RECENT_CONVS_MAX);
  } catch {
    return [];
  }
}

function saveRecentConvs(shopId: number | null | undefined, list: RecentConv[]): void {
  try {
    localStorage.setItem(RECENT_CONVS_KEY(shopId), JSON.stringify(list.slice(0, RECENT_CONVS_MAX)));
  } catch { /* ignore quota */ }
}

function convLabel(conv: ConversationResponse) {
  const v = conv.vehicle;
  if (v) {
    const name = [v.year, v.make, v.model].filter(Boolean).join(' ');
    if (name) return name;
    if (v.plate) return v.plate;
  }
  return conv.title || `Chat #${conv.id}`;
}

function chargerLabel(conv: ConversationResponse): string | null {
  const names = conv.charger_names ?? [];
  if (names.length === 0) return conv.created_by_user_name ?? null;
  if (names.length <= 2) return names.join(', ');
  return `${names[0]}, ${names[1]} +${names.length - 2}`;
}

interface SidebarRowLines {
  owner: string | null;   // customer first name (if known)
  model: string | null;   // year make model
  plate: string | null;
  date: string | null;    // short month+day from updated_at / created_at
  charges: string | null;
  fallback: string;
}

function convLines(conv: ConversationResponse): SidebarRowLines {
  const v = conv.vehicle;
  const ownerName = (v as { first_name?: string; last_name?: string } | null);
  const owner = ownerName
    ? [ownerName.first_name, ownerName.last_name].filter((s) => (s ?? '').toString().trim()).join(' ').trim() || null
    : null;
  const plate = v?.plate || null;
  const model = v ? [v.year, v.make, v.model].filter(Boolean).join(' ') || null : null;
  const iso = conv.updated_at || conv.created_at;
  let date: string | null = null;
  if (iso) {
    try { date = new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { /* ignore */ }
  }
  return {
    owner,
    model,
    plate,
    date,
    charges: chargerLabel(conv),
    fallback: conv.title || `Chat #${conv.id}`,
  };
}

function SidebarRowBody({
  conv,
  isActive,
  unread,
}: {
  conv: ConversationResponse;
  isActive: boolean;
  unread: boolean;
}) {
  const lines = convLines(conv);
  const primary = isActive ? '#F4F4F5' : unread ? '#F4F4F5' : '#A1A1AA';
  const primaryWeight = isActive || unread ? 600 : 400;
  // Three-line layout per user spec:
  //   1) owner first name (prominent) — falls back to model when no owner
  //   2) car YMM (only when owner takes line 1; otherwise this IS line 1)
  //   3) plate · date · charger (muted, dot-separated, any missing skipped)
  const headline = lines.owner || lines.model || lines.fallback;
  const sub = lines.owner ? lines.model : null;
  const metaParts = [lines.plate, lines.date, lines.charges].filter(Boolean) as string[];
  const meta = metaParts.length ? metaParts.join(' · ') : null;
  // Single shield, immediately left of the title. Reads from
  // conv.quote_legal_safe via AuthorizedDot (green-filled = safe,
  // yellow-outlined = needs sig / no in_batch items).
  return (
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Group gap={6} wrap="nowrap" align="center">
        <AuthorizedDot conv={conv} size={13} conversationId={conv.id} />
        <Text size="sm" truncate style={{ color: primary, fontWeight: primaryWeight, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
          {headline}
        </Text>
      </Group>
      {sub && (
        <Text size="11px" truncate style={{ color: isActive ? '#D4D4D8' : '#A1A1AA', lineHeight: 1.3 }}>
          {sub}
        </Text>
      )}
      {meta && (
        <Text size="11px" truncate style={{ color: '#71717A', lineHeight: 1.3 }}>
          {meta}
        </Text>
      )}
    </Box>
  );
}

export function ShopLayout() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const navigateGuarded = useCallback((path: string) => {
    if (processingState.isProcessing && !window.confirm('AI is generating a response. Leave this conversation?')) return;
    // Preserve current tab param when switching conversations
    const currentTab = new URLSearchParams(window.location.search).get('tab');
    navigate(currentTab ? `${path}?tab=${currentTab}` : path);
  }, [navigate]);
  const { conversationId } = useParams<{ conversationId: string }>();
  const [sidebarSearch, setSidebarSearch] = useState('');
  const debouncedSearch = useDebounce(sidebarSearch, 300);
  const { conversations, isLoading, createConversation, deleteConversation, updateConversationStatus } = useConversations(debouncedSearch || undefined);
  // Row that's currently hovered — drives the per-row 3-dots menu visibility
  // (menu also shows on the active row). null = none hovered.
  const [hoveredConvId, setHoveredConvId] = useState<number | null>(null);
  const [rowMenuOpenId, setRowMenuOpenId] = useState<number | null>(null);

  // Recent-conversations per-device cache. Pushes the currently-open conv
  // to the top whenever the URL changes AND we have its data loaded.
  // Items stay until LRU-pruned (cap 10) or localStorage is cleared.
  const [recentConvs, setRecentConvs] = useState<RecentConv[]>(() => loadRecentConvs(user?.shop_id));
  const [recentOpen, setRecentOpen] = useState(false);
  // Reload the per-shop bucket once the shop is known / when it changes (user
  // resolves async after mount; shop switch reloads the app, but guard anyway).
  // Also drop the legacy global key so its cross-shop list never reappears.
  useEffect(() => {
    setRecentConvs(loadRecentConvs(user?.shop_id));
    try { localStorage.removeItem(RECENT_CONVS_KEY_LEGACY); } catch { /* ignore */ }
  }, [user?.shop_id]);
  useEffect(() => {
    if (!conversationId) return;
    const conv = conversations.find((c) => String(c.id) === conversationId);
    if (!conv) return;
    const lines = convLines(conv);
    const entry: RecentConv = {
      id: conv.id,
      owner: lines.owner,
      model: lines.model,
      plate: lines.plate,
      charger: lines.charges,
      viewedAt: Date.now(),
    };
    setRecentConvs((prev) => {
      const next = [entry, ...prev.filter((r) => r.id !== conv.id)].slice(0, RECENT_CONVS_MAX);
      saveRecentConvs(user?.shop_id, next);
      return next;
    });
  }, [conversationId, conversations, user?.shop_id]);
  const [myShops, setMyShops] = useState<ShopResponse[]>([]);
  useEffect(() => {
    authApi.myShops().then(setMyShops).catch(() => setMyShops([]));
  }, []);
  const [backendModalOpen, setBackendModalOpen] = useState(false);
  const [currentBackend, setCurrentBackend] = useState(getBackendMode);
  // Collapse the left drawer to a thin rail (reopen on top, gear on bottom). Persisted.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('shop_nav_collapsed') === '1');
  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem('shop_nav_collapsed', next ? '1' : '0');
    return next;
  });
  const logoTapCount = useRef(0);
  const logoTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLogoClick = () => {
    logoTapCount.current += 1;
    if (logoTapTimer.current) clearTimeout(logoTapTimer.current);
    logoTapTimer.current = setTimeout(() => { logoTapCount.current = 0; }, 2000);
    if (logoTapCount.current >= 20) {
      logoTapCount.current = 0;
      setCurrentBackend(getBackendMode());
      setBackendModalOpen(true);
    }
  };

  const handleNewChat = () => {
    // New RO is now a client-side draft (NewRoPage) — the conversation isn't
    // persisted until the user presses Create there. No more empty placeholder
    // conversations / empty-reuse.
    navigate('/shop/conversations/new');
  };

  // Mark current conversation as read whenever it changes
  useEffect(() => {
    if (!conversationId) return;
    const conv = conversations.find((c) => String(c.id) === conversationId);
    if (conv) markConversationRead(conv.id, conv.updated_at);
  }, [conversationId, conversations]);

  const PINNED_KEY = 'haist_pinned_conversations';
  const getPinnedIds = useCallback((): number[] => {
    try { return JSON.parse(localStorage.getItem(PINNED_KEY) ?? '[]'); } catch { return []; }
  }, []);
  const [pinnedIds, setPinnedIds] = useState<number[]>(getPinnedIds);

  // Refresh pinned list on focus (so changes from JobPage reflect here)
  useEffect(() => {
    const onFocus = () => setPinnedIds(getPinnedIds());
    window.addEventListener('focus', onFocus);
    // Also listen to storage events for cross-tab sync
    const onStorage = (e: StorageEvent) => { if (e.key === PINNED_KEY) setPinnedIds(getPinnedIds()); };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('focus', onFocus); window.removeEventListener('storage', onStorage); };
  }, [getPinnedIds]);

  // ── Per-row conversation actions (moved here from the JobPage header) ──
  const togglePin = useCallback((id: number) => {
    const pinned = getPinnedIds();
    const next = pinned.includes(id) ? pinned.filter((x) => x !== id) : [...pinned, id];
    localStorage.setItem(PINNED_KEY, JSON.stringify(next));
    setPinnedIds(next);
    // Notify JobPage (same-tab) + other tabs.
    window.dispatchEvent(new StorageEvent('storage', { key: PINNED_KEY }));
  }, [getPinnedIds]);

  const handleRowArchive = useCallback(async (conv: ConversationResponse) => {
    await updateConversationStatus(conv.id, conv.status === 'archived' ? 'active' : 'archived');
  }, [updateConversationStatus]);

  const handleRowNewVisit = useCallback(async (conv: ConversationResponse) => {
    const v = conv.vehicle;
    if (!v) return;
    try {
      const newId = await createConversation();
      await vehicleApi.patch(newId, {
        make: v.make, model: v.model, year: v.year,
        plate: v.plate, state: v.state, mileage: v.mileage,
        first_name: v.first_name, last_name: v.last_name,
        phone: v.phone, sub_phones: v.sub_phones, email: v.email,
      } as Parameters<typeof vehicleApi.patch>[1]);
      navigate(`/shop/conversations/${newId}`);
    } catch { /* silent */ }
  }, [createConversation, navigate]);

  const handleRowDelete = useCallback(async (conv: ConversationResponse) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await deleteConversation(conv.id);
      if (String(conv.id) === conversationId) navigate('/shop/dashboard', { replace: true });
    } catch { /* silent */ }
  }, [deleteConversation, conversationId, navigate]);

  // The 3-dots actions menu, shared by the pinned + filtered sidebar rows.
  // Shows on hover, on the active row, or while its own dropdown is open.
  const renderRowMenu = (conv: ConversationResponse, isActive: boolean) => {
    const visible = hoveredConvId === conv.id || isActive || rowMenuOpenId === conv.id;
    if (!visible) return null;
    const isPinned = pinnedIds.includes(conv.id);
    const isArchived = conv.status === 'archived';
    return (
      <Box style={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 3 }}>
        <Menu
          shadow="md"
          width={170}
          position="bottom-end"
          withinPortal
          onOpen={() => setRowMenuOpenId(conv.id)}
          onClose={() => setRowMenuOpenId((cur) => (cur === conv.id ? null : cur))}
        >
          <Menu.Target>
            <ActionIcon variant="filled" color="dark" size={26} radius="xl" onClick={(e) => e.stopPropagation()} style={{ background: '#27272A', color: '#A1A1AA' }}>
              <IconDots size={15} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown styles={{ dropdown: { background: '#1E1E21', border: `1px solid ${BORDER}` } }}>
            <Menu.Item leftSection={isPinned ? <IconPinFilled size={14} /> : <IconPin size={14} />} onClick={() => togglePin(conv.id)}>
              {isPinned ? 'Unpin' : 'Pin'}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={isArchived ? <IconArchiveOff size={14} /> : <IconArchive size={14} />}
              onClick={() => handleRowArchive(conv)}
            >
              {isArchived ? 'Unarchive' : 'Archive'}
            </Menu.Item>
            {conv.vehicle && (
              <>
                <Menu.Divider />
                <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => handleRowNewVisit(conv)}>New visit</Menu.Item>
              </>
            )}
            <Menu.Divider />
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleRowDelete(conv)}>Delete</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    );
  };

  const [filters, setFiltersState] = useState<FilterState>(() => loadFilters());
  const updateFilters = useCallback((next: FilterState) => {
    setFiltersState(next);
    saveFilters(next);
  }, []);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const filtersActive = !filtersAreDefault(filters);

  const cutoff = useMemo(() => {
    if (filters.timeRange === 'all') return null;
    const days = filters.timeRange === '7d' ? 7 : 30;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
  }, [filters.timeRange]);

  // Pinned items show regardless of filter — pinning IS the user's intent
  // to surface them, the filter shouldn't hide a pinned row.
  const pinnedConvs = useMemo(
    () => conversations.filter((c) => pinnedIds.includes(c.id)),
    [conversations, pinnedIds],
  );

  const filteredConvs = useMemo(() => {
    const statusSet = new Set(filters.statuses.length ? filters.statuses : DEFAULT_FILTERS.statuses);
    return conversations.filter((c) => {
      if (pinnedIds.includes(c.id)) return false; // shown in Pinned section above
      const s = (c.status ?? 'active') as ConvStatusFilter;
      if (!statusSet.has(s)) return false;
      if (cutoff) {
        const t = new Date(c.updated_at ?? c.created_at);
        if (t < cutoff) return false;
      }
      if (filters.assignedToMe) {
        const meId = user?.id;
        if (!meId || !(c.charger_user_ids ?? []).includes(meId)) return false;
      }
      if (filters.hasSignature) {
        if (!c.is_authorized) return false;
      }
      return true;
    });
  }, [conversations, filters, cutoff, pinnedIds, user?.id]);


  const currentConv = conversations.find((c) => String(c.id) === conversationId);
  const headerTitle = currentConv ? convLabel(currentConv) : null;

  return (
    <AppShell
      navbar={{ width: { base: SIDEBAR_WIDTH, sm: collapsed ? RAIL_WIDTH : SIDEBAR_WIDTH }, breakpoint: 'sm' }}
      padding={0}
    >

      <AppShell.Navbar
        style={{
          background: SIDEBAR_BG,
          borderRight: `1px solid ${BORDER}`,
          display: 'flex',
          flexDirection: 'column',
        }}
        p={0}
      >
        {/* Logo */}
        <Box py={18} style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', position: 'relative' }}>
          {currentBackend !== 'prod' && (
            <Box
              onClick={() => { setCurrentBackend(getBackendMode()); setBackendModalOpen(true); }}
              style={{
                position: 'absolute', top: 6, left: 10,
                background: currentBackend === 'local' ? '#7C3AED' : '#D97706',
                color: '#fff',
                fontSize: 9, fontWeight: 700, padding: '2px 6px',
                borderRadius: 20, letterSpacing: '0.06em', lineHeight: '14px',
                cursor: 'pointer', userSelect: 'none',
              }}
            >
              {currentBackend.toUpperCase()}
            </Box>
          )}
          {collapsed ? (
            <UnstyledButton onClick={toggleCollapsed} title="Expand sidebar" style={{ color: '#A1A1AA', display: 'flex', alignItems: 'center' }}>
              <IconLayoutSidebarLeftExpand size={22} />
            </UnstyledButton>
          ) : (
            <img
              src="/images/haist/haist-logo.png"
              alt="HAIST"
              style={{ height: 24, filter: 'invert(1) brightness(2)', cursor: 'pointer', userSelect: 'none' }}
              onClick={handleLogoClick}
            />
          )}
          {!collapsed && (
            <UnstyledButton
              onClick={toggleCollapsed}
              visibleFrom="sm"
              title="Collapse sidebar"
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#71717A', display: 'flex', alignItems: 'center' }}
            >
              <IconLayoutSidebarLeftCollapse size={18} />
            </UnstyledButton>
          )}
        </Box>

        <Modal
          opened={backendModalOpen}
          onClose={() => setBackendModalOpen(false)}
          title="Backend Server"
          size="sm"
          styles={{ title: { fontWeight: 600 } }}
        >
          <Stack gap="xs">
            {(Object.entries(BACKENDS) as [keyof typeof BACKENDS, string][]).map(([mode, url]) => {
              const isActive = currentBackend === mode;
              const modeColor = mode === 'local' ? '#7C3AED' : mode === 'dev' ? '#D97706' : '#2563EB';
              return (
                <UnstyledButton
                  key={mode}
                  onClick={() => { setBackendModalOpen(false); if (mode !== currentBackend) setBackendMode(mode); }}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: `2px solid ${isActive ? modeColor : '#27272A'}`,
                    background: isActive ? `${modeColor}22` : 'transparent',
                    transition: 'border-color 0.15s',
                  }}
                >
                  <Group gap="xs" mb={2}>
                    <Text size="xs" fw={700} tt="uppercase" style={{ color: isActive ? modeColor : '#71717A' }}>{mode}</Text>
                    {isActive && <Text size="10px" fw={600} style={{ color: modeColor }}>● active</Text>}
                  </Group>
                  <Text size="11px" c="#52525B" style={{ wordBreak: 'break-all' }}>{url}</Text>
                </UnstyledButton>
              );
            })}
          </Stack>
        </Modal>

        {collapsed && <Box style={{ flex: 1 }} />}
        {!collapsed && (<>
        {/* Top nav */}
        <Box px={1} style={{ flexShrink: 0 }}>
          <NavLink to="/shop/dashboard" style={({ isActive }) => navItemStyle(isActive)}>
            <IconLayoutDashboard size={16} />
            Dashboard
          </NavLink>
          <ConversationsNavGroup />
          <NavLink to="/shop/sms" style={({ isActive }) => navItemStyle(isActive)}>
            <IconMessage size={16} />
            SMS Inbox
          </NavLink>
        </Box>

        {/* Search → Recent → New Chat. Order per user spec: search is the
            primary lookup affordance, Recent is the per-device shortcut for
            jobs the writer just had open, New Chat is the lower-traffic
            create action. */}
        <Box px={12} py={8} style={{ flexShrink: 0 }}>
          <Group gap={6} wrap="nowrap" align="center" mb={6}>
            <TextInput
              placeholder="Search name, plate, phone..."
              leftSection={<IconSearch size={13} color="#52525B" />}
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.currentTarget.value)}
              size="xs"
              style={{ flex: 1 }}
              styles={{
                input: {
                  background: 'transparent',
                  border: `1px solid ${BORDER}`,
                  color: '#F4F4F5',
                  fontSize: 12,
                },
              }}
            />
            <Popover
              opened={filterPopoverOpen}
              onChange={setFilterPopoverOpen}
              position="bottom-end"
              width={260}
              shadow="md"
              withinPortal
            >
              <Popover.Target>
                <ActionIcon
                  variant="default"
                  size="md"
                  onClick={() => setFilterPopoverOpen((o) => !o)}
                  title="Filter conversations"
                  style={{
                    background: filtersActive ? 'rgba(37,99,235,0.16)' : 'transparent',
                    border: `1px solid ${filtersActive ? BLUE : BORDER}`,
                    position: 'relative',
                  }}
                >
                  <IconFilter size={14} color={filtersActive ? '#93C5FD' : '#A1A1AA'} />
                  {filtersActive && (
                    <Box style={{
                      position: 'absolute', top: -3, right: -3,
                      width: 8, height: 8, borderRadius: '50%',
                      background: BLUE,
                    }} />
                  )}
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown style={{ background: '#18181B', border: `1px solid ${BORDER}` }}>
                <Stack gap={12}>
                  <Box>
                    <Text size="xs" fw={600} c="#A1A1AA" tt="uppercase" mb={6} style={{ letterSpacing: 0.6 }}>Status</Text>
                    <Stack gap={4}>
                      {(['active', 'repair', 'archived', 'completed'] as ConvStatusFilter[]).map((s) => (
                        <Checkbox
                          key={s}
                          size="xs"
                          checked={filters.statuses.includes(s)}
                          label={<Text size="xs" c="#F4F4F5" tt="capitalize">{s}</Text>}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            const nextStatuses = checked
                              ? Array.from(new Set([...filters.statuses, s]))
                              : filters.statuses.filter((x) => x !== s);
                            updateFilters({ ...filters, statuses: nextStatuses });
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Divider color={BORDER} />
                  <Box>
                    <Text size="xs" fw={600} c="#A1A1AA" tt="uppercase" mb={6} style={{ letterSpacing: 0.6 }}>Time</Text>
                    <SegmentedControl
                      fullWidth
                      size="xs"
                      value={filters.timeRange}
                      onChange={(v) => updateFilters({ ...filters, timeRange: v as TimeRangeFilter })}
                      data={[
                        { label: '7d', value: '7d' },
                        { label: '30d', value: '30d' },
                        { label: 'All', value: 'all' },
                      ]}
                      styles={{
                        root: { background: '#0C0C0E', border: `1px solid ${BORDER}` },
                        indicator: { background: '#27272A' },
                        label: { color: '#F4F4F5' },
                      }}
                    />
                  </Box>
                  <Divider color={BORDER} />
                  <Switch
                    size="xs"
                    label={<Text size="xs" c="#F4F4F5">Assigned to me</Text>}
                    checked={filters.assignedToMe}
                    onChange={(e) => updateFilters({ ...filters, assignedToMe: e.currentTarget.checked })}
                  />
                  <Switch
                    size="xs"
                    label={<Text size="xs" c="#F4F4F5">Legal doc on file</Text>}
                    checked={filters.hasSignature}
                    onChange={(e) => updateFilters({ ...filters, hasSignature: e.currentTarget.checked })}
                  />
                  {filtersActive && (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => updateFilters(DEFAULT_FILTERS)}
                    >
                      Reset to default
                    </Button>
                  )}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>

          {/* Recent Conversations — per-device cache (localStorage). Push
              fires whenever the user opens a conv. Click an entry to jump
              back. The trigger ALWAYS renders (was conditional on
              recentConvs.length>0, which made the whole feature invisible
              on a fresh device) — empty state shows a "No recent" line so
              the user knows the dropdown exists. */}
          <Popover
            opened={recentOpen}
            onChange={setRecentOpen}
            position="bottom-start"
            width={300}
            shadow="md"
            withinPortal
          >
            <Popover.Target>
              <UnstyledButton
                onClick={() => setRecentOpen((o) => !o)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 6,
                  border: `1px solid ${BORDER}`, color: '#A1A1AA',
                  fontSize: 12, marginBottom: 6,
                }}
              >
                <IconCar size={14} stroke={1.5} />
                <Text size="12px" fw={600} style={{ flex: 1, textAlign: 'left' }}>
                  Recent{recentConvs.length > 0 ? ` · ${recentConvs.length}` : ''}
                </Text>
                <IconChevronDown size={12} stroke={1.75} style={{
                  transform: recentOpen ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 150ms',
                }} />
              </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown p={6} style={{ background: '#18181B', border: `1px solid ${BORDER}` }}>
              {recentConvs.length === 0 ? (
                <Box p={10}>
                  <Text size="sm" c="#A1A1AA">No recent conversations yet.</Text>
                  <Text size="xs" c="#71717A" mt={4}>Open any conversation to start tracking recents on this device.</Text>
                </Box>
              ) : (
                <Stack gap={2}>
                  {recentConvs.map((rc) => {
                    const isActive = conversationId === String(rc.id);
                    const headline = rc.owner || rc.model || `Chat #${rc.id}`;
                    const sub = rc.owner ? rc.model : null;
                    const metaParts = [rc.plate, rc.charger].filter(Boolean) as string[];
                    return (
                      <UnstyledButton
                        key={rc.id}
                        onClick={() => { setRecentOpen(false); navigateGuarded(`/shop/conversations/${rc.id}`); }}
                        px={10}
                        py={6}
                        style={{
                          borderRadius: 6,
                          background: isActive ? 'rgba(37,99,235,0.16)' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                        onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <Text size="13px" fw={600} c="#F4F4F5" truncate>{headline}</Text>
                        {sub && <Text size="11px" c="#A1A1AA" truncate>{sub}</Text>}
                        {metaParts.length > 0 && (
                          <Text size="11px" c="#71717A" truncate>{metaParts.join(' · ')}</Text>
                        )}
                      </UnstyledButton>
                    );
                  })}
                </Stack>
              )}
            </Popover.Dropdown>
          </Popover>

          {/* New Chat — moved below Search + Recent per user spec. */}
          <Group gap={6} wrap="nowrap">
            <UnstyledButton
              onClick={handleNewChat}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 10px',
                borderRadius: 6,
                border: `1px solid ${BORDER}`,
                color: '#71717A',
                fontSize: 12,
                transition: 'border-color 0.12s, color 0.12s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = '#3F3F46';
                (e.currentTarget as HTMLElement).style.color = '#F4F4F5';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = BORDER;
                (e.currentTarget as HTMLElement).style.color = '#71717A';
              }}
            >
              <IconPlus size={13} />
              New chat
            </UnstyledButton>
          </Group>
        </Box>

        <ScrollArea style={{ flex: 1 }} scrollbarSize={4}>
          {isLoading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <Loader size={14} color="gray" />
            </Box>
          ) : (
            <>
              {/* Pinned section */}
              {pinnedConvs.length > 0 && (
                <>
                  <Box style={{ padding: '8px 16px 2px' }}>
                    <Text size="10px" fw={600} c="#52525B" style={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Pinned</Text>
                  </Box>
                  {pinnedConvs.map((conv) => {
                    const isActive = conversationId === String(conv.id);
                    const unread = !isActive && isUnread(conv.id, conv.updated_at);
                    const menuVisible = hoveredConvId === conv.id || isActive || rowMenuOpenId === conv.id;
                    return (
                      <Box
                        key={conv.id}
                        style={{ position: 'relative' }}
                        onMouseEnter={() => setHoveredConvId(conv.id)}
                        onMouseLeave={() => setHoveredConvId((cur) => (cur === conv.id ? null : cur))}
                      >
                        <UnstyledButton
                          onClick={() => navigateGuarded(`/shop/conversations/${conv.id}`)}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 16px',
                            borderLeft: isActive ? `2px solid ${BLUE}` : '2px solid transparent',
                            marginLeft: -1,
                            background: isActive ? 'rgba(37,99,235,0.08)' : 'transparent',
                            transition: 'background 0.12s',
                          }}
                          onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                          onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          <IconPinFilled size={11} color={isActive ? BLUE : '#52525B'} style={{ flexShrink: 0, marginTop: 2 }} />
                          <SidebarRowBody conv={conv} isActive={isActive} unread={unread} />
                          {unread && !menuVisible && <Box style={{ width: 7, height: 7, borderRadius: '50%', background: BLUE, flexShrink: 0 }} />}
                        </UnstyledButton>
                        {renderRowMenu(conv, isActive)}
                      </Box>
                    );
                  })}
                  <Box style={{ height: 1, background: BORDER, margin: '6px 16px' }} />
                </>
              )}

              {/* Filtered conversation list — replaces the old Active/Archived
                  split. The filter popover near the search box decides what
                  lands here; status is shown per-row via the colored side
                  border + an icon for archived items. */}
              {filteredConvs.length === 0 ? (
                <Text size="xs" c="#52525B" px={16} py={6}>
                  {filtersActive ? 'No conversations match the active filters.' : 'No active conversations'}
                </Text>
              ) : filteredConvs.map((conv) => {
                const isActive = conversationId === String(conv.id);
                const unread = !isActive && isUnread(conv.id, conv.updated_at);
                const isArchived = conv.status === 'archived';
                const accent = isArchived ? '#F59E0B' : BLUE;
                const accentBg = isArchived ? 'rgba(245,158,11,0.08)' : 'rgba(37,99,235,0.08)';
                // 3-dots shows on hover, on the active row, or while its own menu is open.
                const menuVisible = hoveredConvId === conv.id || isActive || rowMenuOpenId === conv.id;
                return (
                  <Box
                    key={conv.id}
                    style={{ position: 'relative' }}
                    onMouseEnter={() => setHoveredConvId(conv.id)}
                    onMouseLeave={() => setHoveredConvId((cur) => (cur === conv.id ? null : cur))}
                  >
                    <UnstyledButton
                      onClick={() => navigateGuarded(`/shop/conversations/${conv.id}`)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 16px',
                        borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
                        marginLeft: -1,
                        background: isActive ? accentBg : 'transparent',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      {isArchived && (
                        <IconArchive size={13} color={isActive ? '#F59E0B' : '#52525B'} style={{ flexShrink: 0, marginTop: 2 }} />
                      )}
                      {isArchived ? (
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="xs" truncate style={{ color: isActive ? '#F4F4F5' : unread ? '#F4F4F5' : '#71717A', fontWeight: (isActive || unread) ? 600 : 400 }}>
                            {convLabel(conv)}
                          </Text>
                          {chargerLabel(conv) && (
                            <Text size="11px" truncate style={{ color: '#71717A', lineHeight: 1.3 }}>{chargerLabel(conv)}</Text>
                          )}
                        </Box>
                      ) : (
                        <SidebarRowBody conv={conv} isActive={isActive} unread={unread} />
                      )}
                      {/* Unread dot hides under the 3-dots so they don't overlap. */}
                      {unread && !menuVisible && <Box style={{ width: 7, height: 7, borderRadius: '50%', background: accent, flexShrink: 0 }} />}
                    </UnstyledButton>
                    {/* Per-row actions — moved here from the JobPage header. Sibling
                        of the nav button (not nested) so it's valid HTML + clicks
                        don't trigger navigation. */}
                    {renderRowMenu(conv, isActive)}
                  </Box>
                );
              })}
            </>
          )}
        </ScrollArea>
        </>)}

        <Box style={{ height: 1, background: BORDER, margin: '0 16px' }} />

        {collapsed ? (
          <Group justify="center" py={12} style={{ flexShrink: 0 }}>
            <Menu position="right-end" withArrow shadow="md">
              <Menu.Target>
                <UnstyledButton style={{ color: '#A1A1AA', display: 'flex', alignItems: 'center' }} title="Settings">
                  <IconSettings size={20} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => navigate('/shop/settings')}>
                  Settings
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={() => { logout(); navigate('/shop/login'); }}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        ) : (
        <Group gap={10} px={16} py={12} align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Avatar size={26} radius="xl" color="haist">
            {user?.name?.[0]}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} c="white" truncate lh={1.2}>
              {user?.name}
            </Text>
            {(() => {
              const active = myShops.find((s) => s.id === user?.shop_id);
              return active ? (
                <Text size="xs" c="#71717A" truncate lh={1.2}>
                  {active.name}
                </Text>
              ) : null;
            })()}
          </Box>

          {/* Settings menu — also hosts Sign out */}
          <Menu position="top-end" withArrow shadow="md">
            <Menu.Target>
              <UnstyledButton
                style={{ color: '#71717A', display: 'flex', alignItems: 'center' }}
                title="Settings"
              >
                <IconSettings size={14} />
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconSettings size={14} />}
                onClick={() => navigate('/shop/settings')}
              >
                Settings
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconLogout size={14} />}
                color="red"
                onClick={() => { logout(); navigate('/shop/login'); }}
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          {myShops.length > 1 && (
            <Menu position="top-end" withArrow shadow="md">
              <Menu.Target>
                <UnstyledButton
                  style={{ color: '#71717A', display: 'flex', alignItems: 'center' }}
                  title="Switch shop"
                >
                  <IconBuildingStore size={14} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Switch shop</Menu.Label>
                {myShops.map((s) => (
                  <Menu.Item
                    key={s.id}
                    leftSection={s.id === user?.shop_id ? <IconCheck size={12} /> : <Box w={12} />}
                    onClick={async () => {
                      if (s.id === user?.shop_id) return;
                      try {
                        await authApi.switchShop(s.id);
                        // Refresh cached user BEFORE the hard reload so the
                        // post-reload boot reads the fresh shop_id from
                        // localStorage instead of the stale one.
                        await refreshUser();
                        window.location.href = '/shop/dashboard';
                      } catch {
                        // swallow — user can retry
                      }
                    }}
                  >
                    {s.name}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
        )}
      </AppShell.Navbar>

      <AppShell.Main
        style={{ background: '#0C0C0E', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <Outlet />
      </AppShell.Main>

    </AppShell>
  );
}
